import React, { useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import {
  PriceEvolutionChart,
  PriceEvolutionByCategory,
} from '../../../PriceEvolutionChart'
import { formatPriceM2EvolutionPrice } from '../../../PriceM2EvolutionChart'

import { useRentalMarket } from '../../RentalMarketContext'

interface RentalPriceM2EvolutionProps {
  t: TFunction
}

export const RentalPriceM2Evolution: React.FC<RentalPriceM2EvolutionProps> = ({
  t,
}) => {
  const { filters, service, priceMode } = useRentalMarket()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<PriceEvolutionByCategory>({})
  const [currentView, setCurrentView] = useState<
    'absolute' | 'index' | 'growth' | 'quantile'
  >('absolute')

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Determine location type and value from filters
        let locationType = undefined
        let locationValue = undefined

        if (filters.location) {
          switch (filters.locationType) {
            case 'postal_code':
              locationType = 'postal_code_name'
              break
            case 'city':
              locationType = 'municipality_name'
              break
            case 'district':
              locationType = 'district_name'
              break
          }
          locationValue = filters.location
        }

        const apiFilters = {
          deal_type: 'rent', // Changed from 'purchase' to 'rent'
          property_category:
            filters.propertyCategory.length > 0
              ? filters.propertyCategory
              : undefined,
          location: locationValue,
          location_type: locationType,
          price_field: 'price_per_square_meter',
          outlier_field: 'price_per_square_meter',
          outlier_percentage: 2,
        }

        const response = await service.getRentalPriceM2EvolutionData(apiFilters)
        setData(response.aggregations)
      } catch (err) {
        console.error('Error fetching rental price/m² evolution data:', err)
        setError(t('market.common.errors.loadingData'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filters])

  return (
    <PriceEvolutionChart
      data={data}
      loading={loading}
      error={error}
      currentView={currentView}
      onViewChange={setCurrentView}
      isRental={true} // Changed to true for rental
      isSqm={true}
      priceMode={priceMode}
      title={t('rental.sections.priceM2Evolution.title')}
      formatValue={formatPriceM2EvolutionPrice}
      yAxisFormatter={(value: number) => `CHF ${value.toLocaleString()}/m²`}
      tooltipFormatter={formatPriceM2EvolutionPrice}
      showKPIs={false}
      showInsights={false}
      showQuantileChart={false}
      t={t}
    />
  )
}
